import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, IconButton, Tooltip, Chip, Stack, Typography, Link } from '@mui/material';
import { Visibility as ViewIcon, Receipt as ReceiptIcon } from '@mui/icons-material';
import DataTable from '../../Shared/Components/DataTable/DataTable';
import { ColumnConfig } from '../../Shared/Components/DataTable/DataTable.types';
import ImportStatusBadge from './ImportStatusBadge';
import InvoiceDirectionBadge from './InvoiceDirectionBadge';
import { InvoiceImportHistoryDto, InvoiceImportStatus } from '../Types/invoiceImport.types';
import {
  getInvoiceDirectionDecision,
  getMatchedSubjectRoleLabel,
} from '../Utils/invoiceDirection';

interface ImportHistoryTableProps {
  data: InvoiceImportHistoryDto[];
  isLoading: boolean;
  isError: boolean;
  errorMessage?: string;
  page: number;
  rowsPerPage: number;
  totalCount: number;
  onPageChange: (newPage: number) => void;
  onRowsPerPageChange: (newSize: number) => void;
}

/**
 * Data table displaying invoice import history
 * Shows fileName, status, dates, invoice number, and action buttons
 */
const ImportHistoryTable: React.FC<ImportHistoryTableProps> = ({
  data,
  isLoading,
  isError,
  errorMessage,
  page,
  rowsPerPage,
  totalCount,
  onPageChange,
  onRowsPerPageChange,
}) => {
  const navigate = useNavigate();

  const columns: ColumnConfig<InvoiceImportHistoryDto>[] = [
    {
      key: 'importJobId',
      label: 'ID import jobu',
      render: (row) => row.importJobId,
    },
    {
      key: 'fileName',
      label: 'Nazwa pliku',
      render: (row) => (
        <Box sx={{ maxWidth: 250, overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {row.fileName}
        </Box>
      ),
    },
    {
      key: 'status',
      label: 'Status',
      render: (row) => <ImportStatusBadge status={row.status} statusLabel={row.statusLabel} />,
    },
    {
      key: 'createdAt',
      label: 'Data rozpoczęcia',
      render: (row) => {
        const date = new Date(row.createdAt);
        return (
          <Box>
            <Box>{date.toLocaleDateString('pl-PL')}</Box>
            <Box sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
              {date.toLocaleTimeString('pl-PL', { hour: '2-digit', minute: '2-digit' })}
            </Box>
          </Box>
        );
      },
    },
    {
      key: 'invoiceDirection',
      label: 'Kierunek',
      render: (row) => {
        const decision = getInvoiceDirectionDecision(row.invoiceDirection, row.importerType);
        const matchedSubjectRoleLabel = row.matchedSubjectRole
          ? getMatchedSubjectRoleLabel(row.matchedSubjectRole)
          : null;

        return (
          <Stack spacing={0.5}>
            <InvoiceDirectionBadge direction={row.invoiceDirection} source={row.invoiceDirectionSource} />
            <Typography variant="caption" color="text.secondary">
              {decision.message}
            </Typography>
            {matchedSubjectRoleLabel ? (
              <Typography variant="caption" color="text.secondary">
                Rola: {matchedSubjectRoleLabel}
              </Typography>
            ) : null}
            {row.invoiceDirectionReason ? (
              <Typography variant="caption" color="text.secondary">
                Powód: {row.invoiceDirectionReason}
              </Typography>
            ) : null}
          </Stack>
        );
      },
    },
    {
      key: 'attemptNumber',
      label: 'Próba / operator',
      render: (row) => {
        const attemptLabel = `Próba ${(row.attemptNumber ?? 0) + 1}`;
        const operatorLabel =
          row.userId != null ? `Operator #${row.userId}` : 'Operator: systemowy';

        return (
          <Stack spacing={0.5}>
            <Typography variant="body2">{attemptLabel}</Typography>
            <Typography variant="caption" color="text.secondary">
              {operatorLabel}
            </Typography>
            {row.previousAttemptImportJobId ? (
              <Link
                component="button"
                type="button"
                variant="caption"
                onClick={() => navigate(`/expense-invoices/import/${row.previousAttemptImportJobId}`)}
              >
                Poprzednia próba: #{row.previousAttemptImportJobId}
              </Link>
            ) : null}
            {row.duplicateOfImportJobId ? (
              <Link
                component="button"
                type="button"
                variant="caption"
                onClick={() => navigate(`/expense-invoices/import/${row.duplicateOfImportJobId}`)}
              >
                Duplikat: #{row.duplicateOfImportJobId}
              </Link>
            ) : null}
          </Stack>
        );
      },
    },
    {
      key: 'completedAt',
      label: 'Data zakończenia',
      render: (row) => {
        if (!row.completedAt) return <span style={{ color: '#999' }}>—</span>;
        const date = new Date(row.completedAt);
        return (
          <Box>
            <Box>{date.toLocaleDateString('pl-PL')}</Box>
            <Box sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
              {date.toLocaleTimeString('pl-PL', { hour: '2-digit', minute: '2-digit' })}
            </Box>
          </Box>
        );
      },
    },
    {
      key: 'invoiceNumber',
      label: 'Numer faktury',
      render: (row) => {
        if (!row.invoiceNumber) return <span style={{ color: '#999' }}>—</span>;
        return (
          <Chip
            label={row.invoiceNumber}
            size="small"
            color="default"
            variant="outlined"
            icon={<ReceiptIcon />}
          />
        );
      },
    },
  ];

  const renderCustomActions = (row: InvoiceImportHistoryDto) => {
    const canViewInvoice = row.status === InvoiceImportStatus.Completed && row.invoiceNumber;

    return (
      <Box display="flex" gap={1}>
        {/* View import job details */}
        <Tooltip title="Zobacz szczegóły importu">
          <IconButton
            size="small"
            color="primary"
            onClick={() => navigate(`/expense-invoices/import/${row.importJobId}`)}
          >
            <ViewIcon />
          </IconButton>
        </Tooltip>

        {/* View invoice (if completed) */}
        {canViewInvoice && (
          <Tooltip title="Zobacz fakturę">
            <IconButton
              size="small"
              color="success"
              onClick={() => {
                // Navigate to invoice detail (assumes route /invoices/:id)
                // We don't have invoiceId in HistoryDto, so we need to fetch it or navigate via import detail
                navigate(`/expense-invoices/import/${row.importJobId}`);
              }}
            >
              <ReceiptIcon />
            </IconButton>
          </Tooltip>
        )}
      </Box>
    );
  };

  return (
    <DataTable<InvoiceImportHistoryDto>
      columns={columns}
      data={data}
      isLoading={isLoading}
      isError={isError}
      errorMessage={errorMessage || 'Błąd podczas ładowania historii importu'}
      getRowId={(row) => row.importJobId}
      page={page}
      rowsPerPage={rowsPerPage}
      totalCount={totalCount}
      onPageChange={onPageChange}
      onRowsPerPageChange={onRowsPerPageChange}
      emptyMessage="Brak historii importu"
      renderCustomActions={renderCustomActions}
    />
  );
};

export default ImportHistoryTable;
